<template functional>
  <div class="pf-media"
       :class="`pf-media--${props.type}`">
    <div class="pf-media__inner">
      <div class="pf-media__aspect-ratio" :style="{backgroundColor: props.aspectRatioBcg}">
        <div class="pf-media__aspect-ratio-content">
          <!-- @slot Media content inside aspect ratio box-->
          <slot></slot>
        </div>
      </div>
    </div>

    <div v-if="props.caption"
         class="pf-media__caption">
      <span v-html="props.caption"></span>
    </div>
  </div>
</template>

<script>
  /**
   * Media container.
   * Render a media component (Image / video...) inside an aspect ratio box from type.
   */
  export default {
    name: 'PFMedia',

    props: {
      /**
       * Aspect ratio type. Available values: full-width, large (960 * 535), video (1920 * 1080), Miny Story (375 * 500)
       */
      type: {
        type: String,
        required: true
      },
      /**
       * Background color for aspect ratio box
       */
      aspectRatioBcg: {
        type: String,
        default: 'transparent'
      },
      /**
       * Caption for media
       */
      caption: {
        type: [String, Boolean],
        default: false
      }
    }
  }
</script>

<style lang="scss">
  // Use style here for nested functionnal components
  // @see: https://github.com/vuejs/vue-loader/issues/1259
  .pf-media {
    &.pf-media--slide-intersticiel,
    &.pf-media--p-footer,
    &.pf-media--large,
    &.pf-media--video {
      @extend %container;
      @include margin-container();
      @include breakpoint('medium-') {
        width: 100%;
      }
    }
  }

  .pf-media .pf-media--prologue {
    @extend %container;
    @include breakpoint('medium-') {
      width: 100%;
    }
  }
</style>

<style lang="scss" scoped>
  .pf-media {
    max-width: $max-width;
    margin-left: auto;
    margin-right: auto;
  }

  .pf-media__inner {
    .pf-media--large & {
      @include width-multi((
        large: 10,
        xlarge: 10
      ));

      @include push-multi((
        large: 1,
        xlarge: 1
      ));
    }

    .pf-media--video & {
      @include width-multi((
        large: 12,
        xlarge: 12
      ));

      width: 100%;
    }
  }

  .pf-media__aspect-ratio {
    max-width: 1440px;
    width: 100%;
    margin: 0 auto;
    @include aspect-ratio(375, 500, 'pf-media__aspect-ratio-content');

    @include breakpoint('medium+') {
      @include aspect-ratio(1280, 800, 'pf-media__aspect-ratio-content');
    }

    /* media types */
    .pf-media--full-width & {
      @include breakpoint('small-') {
        max-width: 375px;
      }
    }

    .pf-media--mini-story & {
      width: 100%;
      @include aspect-ratio(375, 500, 'pf-media__aspect-ratio-content');

      @include breakpoint('small+') {
        max-width: 375px;
      }
    }

    .pf-media--large & {
      width: 100%;
      @include aspect-ratio(960, 535, 'pf-media__aspect-ratio-content');
    }

    .pf-media--video & {
      width: 100%;
      @include aspect-ratio(1920, 1080, 'pf-media__aspect-ratio-content');
    }

    .pf-media--prologue & {
      height: 100%;
      max-width: 138px;
      max-height: 300px;
      @include aspect-ratio(138, 300, 'pf-media__aspect-ratio-content');
    }

    .pf-media--slide-intersticiel & {
      max-width: 760px;
      @include aspect-ratio(760, 480, 'pf-media__aspect-ratio-content');
    }

    .pf-media--p-look & {
      width: 100%;
      @include aspect-ratio(375, 500, 'pf-media__aspect-ratio-content');

      @include breakpoint('medium') {
        @include aspect-ratio(560, 770, 'pf-media__aspect-ratio-content');
      }

      @include breakpoint('large+') {
        @include aspect-ratio(760, 1000, 'pf-media__aspect-ratio-content');
      }
    }
  }

  .pf-media__aspect-ratio-content {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .pf-media__caption {
    text-align: center;
    color: map-get($colors-list, 'white-near');
  }
</style>
